import React, { useState, useEffect } from 'react';
import PageHeader from './components/PageHeader';
import { apiFetch } from './utils/api';

const TOPIC_STATUS = {
  proposed: { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: '💡', label: 'Proposed' },
  approved: { bg: 'bg-green-100', text: 'text-green-800', dot: '✅', label: 'Approved' },
  rejected: { bg: 'bg-red-100', text: 'text-red-800', dot: '❌', label: 'Rejected' },
  used: { bg: 'bg-purple-100', text: 'text-purple-800', dot: '📤', label: 'Used' },
};

const CATEGORY_ICONS = {
  commercial: '🏢',
  domestic: '🏠',
  general: '📌',
};

const TARGET_ICONS = {
  newsletter: '📰',
  blog: '📝',
  social: '📱',
};

function TopicsPage() {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showAddTopic, setShowAddTopic] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [newTopic, setNewTopic] = useState({ title: '', summary: '', category: 'general', target_type: 'newsletter' });

  const loadTopics = async () => {
    try {
      setLoading(true);
      const res = await apiFetch('/api/marketing/topics?limit=200');
      const data = await res.json();
      setTopics(data.topics || []);
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTopics(); }, []);

  const handleApprove = async (id) => {
    setActionLoading(id);
    try {
      await apiFetch(`/api/marketing/topics/${id}/approve`, { method: 'PATCH' });
      setTopics(prev => prev.map(t => t.id === id ? { ...t, status: 'approved', approved_at: new Date().toISOString() } : t));
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id) => {
    setActionLoading(id);
    try {
      await apiFetch(`/api/marketing/topics/${id}/reject`, { method: 'PATCH' });
      setTopics(prev => prev.map(t => t.id === id ? { ...t, status: 'rejected', rejected_at: new Date().toISOString() } : t));
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this topic?')) return;
    setActionLoading(id);
    try {
      await apiFetch(`/api/marketing/topics/${id}`, { method: 'DELETE' });
      setTopics(prev => prev.filter(t => t.id !== id));
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddTopic = async (e) => {
    e.preventDefault();
    if (!newTopic.title.trim()) return;
    try {
      const res = await apiFetch('/api/marketing/topics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newTopic, source: 'manual' }),
      });
      const created = await res.json();
      setTopics(prev => [created, ...prev]);
      setNewTopic({ title: '', summary: '', category: 'general', target_type: 'newsletter' });
      setShowAddTopic(false);
    } catch (e) {
      setLoadError(e.message);
    }
  };

  // Filters
  const filtered = topics.filter(t => {
    if (filter !== 'all' && t.status !== filter) return false;
    if (categoryFilter !== 'all' && t.category !== categoryFilter) return false;
    return true;
  });

  const proposedCount = topics.filter(t => t.status === 'proposed').length;
  const approvedCount = topics.filter(t => t.status === 'approved').length;
  const rejectedCount = topics.filter(t => t.status === 'rejected').length;
  const usedCount = topics.filter(t => t.status === 'used').length;

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Topics"
        subtitle="Research pipeline — approve ideas for campaigns or add your own"
        backTo="/marketing-hub"
        backLabel="Marketing Hub"
        sticky
      />

      <div className="max-w-6xl mx-auto px-4 py-6">
        {loadError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-red-700 font-medium">⚠️ {loadError}</p>
          </div>
        )}

        {/* Proposed alert */}
        {proposedCount > 0 && (
          <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-lg">💡</span>
              <p className="text-sm text-yellow-800 font-medium">{proposedCount} topic{proposedCount > 1 ? 's' : ''} awaiting review</p>
            </div>
            <button onClick={() => setFilter('proposed')} className="text-xs px-3 py-1.5 bg-yellow-600 text-white rounded font-medium hover:bg-yellow-700">
              Review Now
            </button>
          </div>
        )}

        {/* Status filters */}
        <div className="flex gap-2 flex-wrap mb-4">
          {[
            { key: 'all', label: 'All', count: topics.length },
            { key: 'proposed', label: '💡 Proposed', count: proposedCount },
            { key: 'approved', label: '✅ Approved', count: approvedCount },
            { key: 'used', label: '📤 Used', count: usedCount },
            { key: 'rejected', label: '❌ Rejected', count: rejectedCount },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                filter === f.key
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-blue-300'
              }`}
            >
              {f.label} ({f.count})
            </button>
          ))}

          <div className="ml-auto">
            <button
              onClick={() => setShowAddTopic(!showAddTopic)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              + Add Topic
            </button>
          </div>
        </div>

        {/* Category filter */}
        <div className="flex gap-2 mb-4">
          {[
            { key: 'all', label: 'All Categories' },
            { key: 'commercial', label: '🏢 Commercial' },
            { key: 'domestic', label: '🏠 Domestic' },
            { key: 'general', label: '📌 General' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setCategoryFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                categoryFilter === f.key
                  ? 'bg-gray-800 text-white border-gray-800'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-gray-500'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Add topic form */}
        {showAddTopic && (
          <form onSubmit={handleAddTopic} className="bg-white rounded-lg border border-blue-300 p-5 mb-6 space-y-3">
            <h3 className="text-sm font-semibold text-gray-800">Add Your Own Topic</h3>
            <input
              type="text"
              placeholder="Topic title"
              value={newTopic.title}
              onChange={e => setNewTopic(prev => ({ ...prev, title: e.target.value }))}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
              autoFocus
            />
            <textarea
              placeholder="Summary — why this topic matters for ADG's audience"
              value={newTopic.summary}
              onChange={e => setNewTopic(prev => ({ ...prev, summary: e.target.value }))}
              rows={3}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
            />
            <div className="flex gap-3">
              <select
                value={newTopic.category}
                onChange={e => setNewTopic(prev => ({ ...prev, category: e.target.value }))}
                className="border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="general">📌 General</option>
                <option value="commercial">🏢 Commercial</option>
                <option value="domestic">🏠 Domestic</option>
              </select>
              <select
                value={newTopic.target_type}
                onChange={e => setNewTopic(prev => ({ ...prev, target_type: e.target.value }))}
                className="border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="newsletter">📰 Newsletter</option>
                <option value="blog">📝 Blog</option>
                <option value="social">📱 Social</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700">
                Add Topic
              </button>
              <button type="button" onClick={() => setShowAddTopic(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded text-sm font-medium hover:bg-gray-300">
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Topic cards */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading topics...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <p className="text-lg mb-2">No topics yet</p>
            <p className="text-sm">The research agent will propose topics automatically, or add your own.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(topic => {
              const st = TOPIC_STATUS[topic.status] || TOPIC_STATUS.proposed;
              const catIcon = CATEGORY_ICONS[topic.category] || '📌';
              const targetIcon = TARGET_ICONS[topic.target_type] || '📰';
              const isAction = actionLoading === topic.id;

              return (
                <div key={topic.id} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-sm transition-shadow">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.bg} ${st.text}`}>
                          {st.dot} {st.label}
                        </span>
                        <span className="text-xs text-gray-500">{catIcon} {topic.category}</span>
                        <span className="text-xs text-gray-500">{targetIcon} {topic.target_type}</span>
                        <span className="text-xs text-gray-400">
                          {topic.source === 'agent' ? '🤖 Agent' : '✍️ Manual'}
                        </span>
                      </div>
                      <h3 className="text-sm font-semibold text-gray-800 mb-1">{topic.title}</h3>
                      {topic.summary && (
                        <p className="text-xs text-gray-600 leading-relaxed">{topic.summary}</p>
                      )}
                      {topic.keywords && topic.keywords.length > 0 && (
                        <div className="flex gap-1 mt-2 flex-wrap">
                          {topic.keywords.map((kw, i) => (
                            <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{kw}</span>
                          ))}
                        </div>
                      )}
                      {topic.research_notes && (
                        <details className="mt-2">
                          <summary className="text-xs text-blue-600 cursor-pointer hover:text-blue-800">Research notes</summary>
                          <p className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">{topic.research_notes}</p>
                        </details>
                      )}
                      <p className="text-xs text-gray-400 mt-2">
                        Added {new Date(topic.created_at).toLocaleDateString()}
                        {topic.approved_at && ` · Approved ${new Date(topic.approved_at).toLocaleDateString()}`}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-1.5 flex-shrink-0">
                      {topic.status === 'proposed' && (
                        <>
                          <button
                            onClick={() => handleApprove(topic.id)}
                            disabled={isAction}
                            className="px-3 py-1.5 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 disabled:opacity-50"
                          >
                            {isAction ? '...' : '✅ Approve'}
                          </button>
                          <button
                            onClick={() => handleReject(topic.id)}
                            disabled={isAction}
                            className="px-3 py-1.5 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 disabled:opacity-50"
                          >
                            {isAction ? '...' : '❌ Reject'}
                          </button>
                        </>
                      )}
                      {topic.status === 'approved' && (
                        <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded text-xs font-medium">
                          Ready for campaign
                        </span>
                      )}
                      <button
                        onClick={() => handleDelete(topic.id)}
                        className="px-2 py-1.5 text-gray-400 hover:text-red-600 text-xs"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Summary footer */}
        <div className="mt-6 text-center text-xs text-gray-400">
          {topics.length} total · {proposedCount} proposed · {approvedCount} approved · {usedCount} used
        </div>
      </div>
    </div>
  );
}

export default TopicsPage;
